import React, { memo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TemporaryStudentAvatarDisplay from "@/components/user/TemporaryStudentAvatarDisplay";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { User, LogOut } from "lucide-react";
import { getBucketForFilename, extractFilenameFromUrl } from "@/utils/avatarUrlUtils";

function getTeacherInitials(
  displayName?: string | null,
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string {
  if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  if (displayName) {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    return displayName.charAt(0).toUpperCase();
  }
  return email?.charAt(0)?.toUpperCase() || "U";
}

const UserMenu: React.FC = memo(() => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const userRole = profile?.role || "student";
  const isStudent = userRole === "student";

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleProfileClick = () => {
    navigate("/profile");
  };

  // Don't render if no user
  if (!user) {
    return null;
  }

  // Mobile and desktop have different avatar sizes
  const avatarSize = isMobile ? "sm" : "xl";
  const buttonSize = isMobile ? "sm" : "icon";
  const avatarDimensions = isMobile ? "w-6 h-6" : "w-14 h-14";

  // For students: prioritize avatar_url (upgraded avatars), fallback to student_avatar_image_temporary (initial avatars)
  const studentAvatarUrl = isStudent
    ? profile?.avatar_url || (profile as any)?.student_avatar_image_temporary // eslint-disable-line @typescript-eslint/no-explicit-any
    : null;

  let bucket: "avatars" | "temporary-student-avatars" = "temporary-student-avatars";
  if (isStudent && profile?.avatar_url) {
    bucket = "avatars";
  } else if (studentAvatarUrl) {
    bucket = getBucketForFilename(extractFilenameFromUrl(studentAvatarUrl));
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={buttonSize}
          className={`rounded-full flex items-center justify-center p-0 ${isMobile ? "h-8 w-8" : "h-18 w-18"}`}
        >
          {isStudent ? (
            <TemporaryStudentAvatarDisplay
              avatarFileName={studentAvatarUrl}
              bucket={bucket}
              size={avatarSize}
              className={`rounded-full border-2 border-primary/20 ${avatarDimensions}`}
            />
          ) : (
            <div
              className={`${avatarDimensions} rounded-full bg-primary text-primary-foreground flex items-center justify-center ${isMobile ? "text-sm" : "text-2xl"} font-semibold border-2 border-primary/20`}
            >
              {getTeacherInitials(
                profile?.display_name,
                profile?.first_name,
                profile?.last_name,
                user?.email
              )}
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`${isMobile ? "w-[200px]" : "w-[220px]"}`}>
        <DropdownMenuItem onClick={handleProfileClick} className="cursor-pointer">
          <User className="mr-2 h-4 w-4" />
          {t("Profil", "Profil")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          {t("Odhlásit", "Odhlásiť")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

UserMenu.displayName = "UserMenu";

export default UserMenu;
